import axios from "axios";
import { Link } from "react-router-dom";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useLocation } from "react-router-dom";
import L from "leaflet";
import { MapContainer, Polygon, TileLayer, WMSTileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import {
  faArrowLeft,
  faEye,
  faImage,
} from "@fortawesome/free-solid-svg-icons";

const containerStyle = {
  width: "100%",
  height: "50vh",
};

const center = {
  lat: 28.641832439372656,
  lng: 77.22480777651072,
};

// Bhuvan WMS Layer URL
const bhuvanLayerUrl = 'https://bhuvan-ras1.nrsc.gov.in/tilecache/tilecache.py';

// Helper component to fit bounds
const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds, map]);

  return null;
};

// Helper function to parse coordinate pairs
const parseCoordinatePair = (pair) => {
  if (!Array.isArray(pair) || pair.length < 2) {
    return null;
  }

  const first = Number(pair[0]);
  const second = Number(pair[1]);

  if (Number.isNaN(first) || Number.isNaN(second)) {
    return null;
  }

  // Check if coordinates need to be swapped (lat/lng format)
  if (Math.abs(first) > 90 && Math.abs(second) <= 90) {
    return [second, first];
  }

  return [first, second];
};

// Helper function to normalize polygon shape data
const normalizePolygonShape = (rawPolygonShape) => {
  if (!rawPolygonShape) {
    return [];
  }

  let parsed = rawPolygonShape;
  if (typeof rawPolygonShape === "string") {
    try {
      parsed = JSON.parse(rawPolygonShape);
    } catch (_) {
      return [];
    }
  }

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return [];
  }

  // Handle array of arrays format
  if (Array.isArray(parsed[0])) {
    return parsed.map(parseCoordinatePair).filter(Boolean);
  }

  // Handle array of objects format
  return parsed
    .map((point) => {
      if (!point || typeof point !== "object") {
        return null;
      }

      const latValue = Number(
        point.lat ?? point.latitude ?? point.Latitude ?? point.y
      );
      const lngValue = Number(
        point.lng ?? point.lon ?? point.longitude ?? point.Longitude ?? point.x
      );

      if (Number.isNaN(latValue) || Number.isNaN(lngValue)) {
        return null;
      }

      return [latValue, lngValue];
    })
    .filter(Boolean);
};

// Custom WMSTileLayer component for Bhuvan
const BhuvanWMSTileLayer = ({ url, layers, ...props }) => {
  const map = useMap();
  
  useEffect(() => {
    const wmsLayer = L.tileLayer.wms(url, {
      layers: layers,
      format: 'image/png',
      transparent: true,
      attribution: 'Bhuvan NRSC',
      maxZoom: 18,
      version: '1.1.1',
      srs: 'EPSG:3857',
      Maxresolution: 156543.033928,
      styles: '',
      ...props
    }).addTo(map);

    return () => {
      map.removeLayer(wmsLayer);
    };
  }, [map, url, layers, props]);

  return null;
};

const NMEOOPDeatilPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = location.state; // initial data passed via router
  const [farmerDetails, setFarmerDetails] = useState(initialState || null);
  const [cropSurveyData, setCropSurveyData] = useState(null);
  const [componentName, setComponentName] = useState("");
  const [activeImage, setActiveImage] = useState(null);
  const [coconutName, setCoconutName] = useState("");

  // Determine schemeCodeModel from sessionStorage
  const schemeCodeModel = sessionStorage.getItem("schemeCodeModel");

  // Flag to ensure fetch is only done once.
  const [fetched, setFetched] = useState(false);

  // When the component mounts (or farmerDetails changes), fetch detailed data if needed
  useEffect(() => {
    if (farmerDetails && !fetched) {
      console.log("Initial farmer details:", farmerDetails);

      if (schemeCodeModel === "14" || !schemeCodeModel) {
        fetchDetailedDataNMEOOP(farmerDetails.farmerCode.farmerCode);
        setFetched(true); // Set flag to true after calling the function
      }
    }
  }, [farmerDetails]);

  async function fetchDetailedDataNMEOOP(value) {
    try {
      const result = await axios.post(
        `${
          process.env.NODE_ENV === "development"
            ? process.env.REACT_APP_DEV_MODE
            : process.env.REACT_APP_PRO_MODE
        }/api/NMEO_OP/getNMEOFarmerDetailsList`,
        {
          schemeCode: "14",
          lngA_FarmerCode: farmerDetails.farmerCode.farmerCode,
          orgTypeCode: "0",
          SurveyUserCode: "0",
          stateCode: 0,
          districtCode: 0,
          subDistrictCode: "0",
          PageNumber: "1",
          villageCode: "0"
        }
      );
      const data = result.data.dataContent[0];
      console.log('mmmmmmmmmmmmmmmmmmmmmmm', data);
      // Update our local state with the fetched details
      setFarmerDetails(data);
      setCropSurveyData(data);
    } catch (error) {
      console.error("Error fetching NMEOOP details:", error);
    }
  }

  // Process polygon paths for Leaflet
  const polygonPaths = useMemo(() => {
    const polygonData = cropSurveyData?.farmerCropSurveyPolygonList || 
                        farmerDetails?.farmerCropSurveyPolygonList || [];
    
    if (!Array.isArray(polygonData) || polygonData.length === 0) {
      return [];
    }

    const shapes = polygonData
      .map((item) => normalizePolygonShape(item?.polygonShape))
      .filter((shape) => shape.length > 0);

    return shapes;
  }, [cropSurveyData, farmerDetails]);

  // Calculate map bounds from polygon paths
  const mapBounds = useMemo(() => {
    if (polygonPaths.length === 0) {
      return null;
    }

    const allPoints = polygonPaths.flat();
    if (allPoints.length === 0) {
      return null;
    }

    return L.latLngBounds(allPoints);
  }, [polygonPaths]);

  // Calculate map center
  const mapCenterLatLng = useMemo(() => {
    if (mapBounds && mapBounds.isValid()) {
      const mapCenterValue = mapBounds.getCenter();
      return [mapCenterValue.lat, mapCenterValue.lng];
    }

    return [center.lat, center.lng];
  }, [mapBounds]);

  // Handle active image click for a modal
  function handleActiveClick(index) {
    setActiveImage(index);
    console.log("Active image index:", index);
    if (cropSurveyData && cropSurveyData.farmerCropSurveyImageList) {
      const imagePath = cropSurveyData.farmerCropSurveyImageList[index].attPath;
      const innerHTML = `<img src="${imagePath}" alt="Farmer Image" class="img-fluid w-100 h-50" />`;
      document.getElementById("modalBody").innerHTML = innerHTML;
    }
  }

  return (
    <main id="main" className="main">
      <div className="pagetitle page_title_box d-flex align-items-center justify-content-between">
        <nav>
          <h1>Survey Details</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Survey</li>
            <li className="breadcrumb-item active">Survey Details View</li>
          </ol>
        </nav>
        <button
          type="button"
          className="btn btn-success"
          onClick={() => navigate("/newadminpanel/farmer")}
        >
          <i>
            <FontAwesomeIcon icon={faArrowLeft} />
          </i>{" "}
          Back
        </button>
      </div>
      <section className="section">
        <div className="row">
          <div className="col-lg-12 dashboard">
            <div className="card">
              {console.log('000000000000000000000000---------------------', cropSurveyData)}
              {cropSurveyData && (schemeCodeModel === "14" || !schemeCodeModel) && (
                <div className="card-body p-5 pt-2">
                  {/* Polygon Details with Bhuvan Map */}
                  <div className="row cropview">
                    <h5 className="formgrouping">Polygon Details</h5>
                    <MapContainer
                      center={mapCenterLatLng}
                      zoom={14}
                      maxZoom={18}
                      style={containerStyle}
                      scrollWheelZoom
                      zoomControl={true}
                    >
                      {/* Bhuvan WMS Base Layer */}
                      <BhuvanWMSTileLayer
                        url={bhuvanLayerUrl}
                        layers="bhuvan_wbase_ind"
                      />
                      
                      {/* Polygon Overlays */}
                      {polygonPaths.map((path, index) => (
                        <Polygon
                          key={`farmer-polygon-${index}`}
                          positions={path}
                          pathOptions={{
                            color: "#FF0000",
                            fillColor: "#FF0000",
                            fillOpacity: 0.1,
                            weight: 2,
                          }}
                          eventHandlers={{
                            mouseover: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 0.3,
                                weight: 3,
                              });
                            },
                            mouseout: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 0.1,
                                weight: 2,
                              });
                            },
                          }}
                        />
                      ))}
                      
                      {/* Auto-fit bounds */}
                      {mapBounds ? <FitBounds bounds={mapBounds} /> : null}
                    </MapContainer>
                  </div>
                  
                  {/* Survey Details */}
                  <h5 className="formgrouping">Survey Details</h5>
                  <div className="row surveydetailsbg">
                    {cropSurveyData.schemeName && (
                      <div className="col-md-3">
                        <h6>Scheme Name</h6>
                        
                        <p>{cropSurveyData.schemeName}</p>
                      </div>
                    )}
                    {cropSurveyData.surveyUserCode && (
                      <div className="col-md-3">
                        <h6>Survey No.</h6>
                        <p>{cropSurveyData.surveyUserCode}</p>
                      </div>
                    )}
                    {cropSurveyData.agencyName && (
                      <div className="col-md-3">
                        <h6>Agency Name</h6>
                        <p>{cropSurveyData.agencyName}</p>
                      </div>
                    )}
                    {cropSurveyData.userName && (
                      <div className="col-md-3">
                        <h6>User Name</h6>
                        <p>{cropSurveyData.userName}</p>
                      </div>
                    )}
                    {cropSurveyData.farmerName && (
                      <div className="col-md-3">
                        <h6>Farmer Name</h6>
                        <p>{cropSurveyData.farmerName}</p>
                      </div>
                    )}
                    {cropSurveyData.farmerID && (
                      <div className="col-md-3">
                        <h6>Farmer Id</h6>
                        <p>{cropSurveyData.farmerID}</p>
                      </div>
                    )}
                    {cropSurveyData.stateName && (
                      <div className="col-md-3">
                        <h6>State</h6>
                        <p>{cropSurveyData.stateName}</p>
                      </div>
                    )}
                    {cropSurveyData.districtName && (
                      <div className="col-md-3">
                        <h6>District</h6>
                        <p>{cropSurveyData.districtName}</p>
                      </div>
                    )}
                    {cropSurveyData.subDistrictName && (
                      <div className="col-md-3">
                        <h6>Sub District</h6>
                        <p>{cropSurveyData.subDistrictName}</p>
                      </div>
                    )}
                    {cropSurveyData.villageName && (
                      <div className="col-md-3">
                        <h6>Village</h6>
                        <p>{cropSurveyData.villageName}</p>
                      </div>
                    )}
                    {cropSurveyData.mobileNo && (
                      <div className="col-md-3">
                        <h6>Mobile No</h6>
                        <p>{cropSurveyData.mobileNo}</p>
                      </div>
                    )}
                    {cropSurveyData.createdOn && (
                      <div className="col-md-3">
                        <h6>Created Date</h6>
                        <p>{cropSurveyData.createdOn.split("T")[0]}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Farmer Crop Stage List */}
                  {cropSurveyData.farmerCropStageList &&
                    cropSurveyData.farmerCropStageList.length > 0 && (
                      <>
                        <h5 className="formgrouping">Farmer Plot Registration List</h5>
                        <div className="table-responsive newtable">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>farmerCode</th>
                                <th>Crop Stage Name</th>
                                <th>Current Date</th>
                                <th>Last Visit Date</th>
                                <th>Created On By UC</th>
                                <th>UserName</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cropSurveyData.farmerCropStageList.map((item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.farmerCode || "N/A"}</td>
                                  <td>{item.cropStageName || "N/A"}</td>
                                  <td>{new Date(item.currentDate).toLocaleDateString("en-CA") || "N/A"}</td>
                                  <td>{new Date(item.lastVisitDate).toLocaleDateString("en-CA") || "N/A"}</td>
                                  <td>{new Date(item.createdOnByUC).toLocaleDateString("en-CA") || "N/A"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                  {/* Farmer Aadhaar Image List */}
                  {cropSurveyData.farmerAadhaarImageList &&
                    cropSurveyData.farmerAadhaarImageList.length > 0 && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Farmer Aadhaar Image</h5>
                        {Object.entries(
                          cropSurveyData.farmerAadhaarImageList.reduce((acc, item) => {
                            if (!acc[item.plotId]) {
                              acc[item.plotId] = [];
                            }
                            acc[item.plotId].push(item);
                            return acc;
                          }, {})
                        ).map(([plotId, images]) => (
                          <div key={plotId} className="row mb-3">
                            {images.map((item, index) => (
                              <React.Fragment key={index}>
                                <div className="col-md-2">
                                  <img
                                    src={item.attPath}
                                    alt="Farmer Image"
                                    data-bs-toggle="modal"
                                    data-bs-target="#staticBackdrop"
                                    className="img-fluid imgdetials"
                                    onClick={() => handleActiveClick(index)}
                                  />
                                </div>
                              </React.Fragment>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Farmer Seed Distribution Image List */}
                  {cropSurveyData.farmerSeedDistributionImageList &&
                    cropSurveyData.farmerSeedDistributionImageList.length > 0 && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Farmer Seed Distribution Image</h5>
                        {Object.entries(
                          cropSurveyData.farmerSeedDistributionImageList.reduce((acc, item) => {
                            if (!acc[item.plotId]) {
                              acc[item.plotId] = [];
                            }
                            acc[item.plotId].push(item);
                            return acc;
                          }, {})
                        ).map(([plotId, images]) => (
                          <div key={plotId} className="row mb-3">
                            {images.map((item, index) => (
                              <React.Fragment key={index}>
                                <div className="col-md-2">
                                  <img
                                    src={item.attPath}
                                    alt="Farmer Image"
                                    data-bs-toggle="modal"
                                    data-bs-target="#staticBackdrop"
                                    className="img-fluid imgdetials"
                                    onClick={() => handleActiveClick(index)}
                                  />
                                </div>
                              </React.Fragment>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Farmer Plot Registration List */}
                  {cropSurveyData.farmerPlotRegistrationList &&
                    cropSurveyData.farmerPlotRegistrationList.length > 0 && (
                      <>
                        <h5 className="formgrouping">Farmer Plot Registration List</h5>
                        <div className="table-responsive newtable">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Farmer Code</th>
                                <th>Plot ID</th>
                                <th>Plot Total Area</th>
                                <th>Plot at Area Cultivation</th>
                                <th>Num of Oil Palm Trees</th>
                                <th>Plant Month Year</th>
                                <th>fbb Production</th>
                                <th>Agency Name</th>
                                <th>Created On</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cropSurveyData.farmerPlotRegistrationList.map((item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.farmerId || "N/A"}</td>
                                  <td>{item.plotId || "N/A"}</td>
                                  <td>{item.plotTotalArea || "N/A"}</td>
                                  <td>{item.plotatAreaCultivation || "N/A"}</td>
                                  <td>{item.numofoilpalmtrees || "N/A"}</td>
                                  <td>{item.plantMonthYear || "N/A"}</td>
                                  <td>{item.fbbProduction || "N/A"}</td>
                                  <td>{item.agencyName || "N/A"}</td>
                                  <td>{item.createdOn || "N/A"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                  {/* Farmer Crop Survey Image List */}
                  {cropSurveyData.farmerCropSurveyImageList &&
                    cropSurveyData.farmerCropSurveyImageList.length > 0 && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Farmer Crop Survey Image</h5>
                        {console.log("Grouped cropSurveyData:", cropSurveyData)}
                        {Object.entries(
                          cropSurveyData.farmerCropSurveyImageList.reduce((acc, item) => {
                            if (!acc[item.plotId]) {
                              acc[item.plotId] = [];
                            }
                            acc[item.plotId].push(item);
                            return acc;
                          }, {})
                        ).map(([plotId, images]) => (
                          <div key={plotId} className="row mb-3">
                            {images.map((item, index) => (
                              <React.Fragment key={index}>
                                <div className="col-md-2">
                                  <img
                                    src={item.attPath}
                                    alt="Farmer Image"
                                    data-bs-toggle="modal"
                                    data-bs-target="#staticBackdrop"
                                    className="img-fluid imgdetials"
                                    onClick={() => handleActiveClick(index)}
                                  />
                                </div>
                              </React.Fragment>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default NMEOOPDeatilPage;